import { useMemo } from "react";
import { CheckboxFilterOption } from "@/shared/components/filter/CheckboxFilterGroup";
import { PathPickFilterGroup } from "@/features/pathpick/components/ui/PathPickFilterPanel";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  PathPickProductFilter,
  PathPickProductFilterKey,
} from "@/features/pathpick/features/search-products/hooks/usePathPickProductFilter";
import useGetPathPickFilterInfo from "@/features/pathpick/features/search-products/hooks/query/useGetPathPickFilterInfo";
import usePathPickCollectionOptions from "@/features/pathpick/features/search-products/hooks/usePathPickCollectionOptions";
import { toPathPickBrandOptions } from "@/features/pathpick/utils";

// 그룹 체크 상태 — 드래프트와 적용값이 같은 규칙으로 읽는다.
export const getPathPickProductFilterValues = (
  source: PathPickProductFilter,
  key: PathPickProductFilterKey
) => (source[key] ?? []) as (string | number)[];

// 그룹 체크 토글 → 드래프트 패치. 드래프트를 클로저로 읽지 않도록 updateDraft에 넘겨 쓴다.
export const togglePathPickProductFilterValue = (
  current: PathPickProductFilter,
  key: PathPickProductFilterKey,
  value: string | number
): Partial<PathPickProductFilter> => {
  const values = getPathPickProductFilterValues(current, key);
  return {
    [key]: values.includes(value)
      ? values.filter((item) => item !== value)
      : [...values, value],
  };
};

// 상품 찾기 상세 필터의 그룹 — 브랜드 / 카테고리 / 상품 유형.
// 인라인 상세 필터와 모바일 필터 모달이 같은 그룹을 쓴다.
//
// 옵션·그룹은 고정 참조로 둔다 — 범위 입력으로 드래프트가 바뀔 때 memo된 그룹 카드
// (특히 옵션이 많은 브랜드)가 다시 그려지지 않게 하기 위해서다.
const usePathPickProductFilterGroups = () => {
  const { t } = useI18nHelper();
  const { pathPickFilterInfo } = useGetPathPickFilterInfo();
  const collectionOptions = usePathPickCollectionOptions();

  const brandOptions = useMemo<CheckboxFilterOption[]>(
    () =>
      toPathPickBrandOptions(pathPickFilterInfo?.brands).map((brand) => ({
        label: brand.name,
        value: brand.id,
      })),
    [pathPickFilterInfo?.brands]
  );

  const categoryOptions = useMemo<CheckboxFilterOption[]>(
    () =>
      (pathPickFilterInfo?.categories ?? []).map((category) => ({
        label: category,
        value: category,
      })),
    [pathPickFilterInfo?.categories]
  );

  return useMemo<PathPickFilterGroup<PathPickProductFilterKey>[]>(
    () => [
      {
        key: "brandId__in",
        title: t("path_pick:filter.brand"),
        options: brandOptions,
      },
      {
        key: "pathradeCategory__in",
        title: t("path_pick:filter.category"),
        options: categoryOptions,
      },
      {
        key: "collection__in",
        title: t("path_pick:filter.product_type"),
        options: collectionOptions,
      },
    ],
    [t, brandOptions, categoryOptions, collectionOptions]
  );
};

export default usePathPickProductFilterGroups;
